import * as df from "date-fns";
import { useMemo } from "react";
import { client } from "@/api-client";
import type {
  SessionStatus,
  SessionWithOwnerDogDisplayListOutput,
  SessionWithTrainerDisplayListOutput,
} from "@/api-types";
import type { Role } from "@/UserContext";

type SessionType<T extends Role> = T extends "owner"
  ? SessionWithTrainerDisplayListOutput
  : SessionWithOwnerDogDisplayListOutput;
type SessionGroups = Record<string, SessionStatus[]>;

export const useSessionsByStatus = <T extends Role>(
  userRole: T,
  statuses: SessionStatus[],
) => {
  const endpoint =
    userRole === "owner" ? "/owner/sessions/" : "/trainer/sessions/";

  const { data: sessions } = client.useQuery("get", endpoint, {
    params: {
      query: {
        statuses,
      },
    },
  });

  const sessionsByStatus: Record<string, SessionType<T>[]> = {};

  // Initialize empty arrays for each status
  statuses.forEach((status) => {
    sessionsByStatus[status] = [];
  });

  // Group sessions by status
  sessions?.sessions.forEach((session) => {
    const sessionList = sessionsByStatus[session.status];
    if (!sessionList) {
      return;
    }
    sessionList.push(session as SessionType<T>);
  });

  return {
    sessions,
    sessionsByStatus,
    isLoading: !sessions,
  };
};

export const useGroupedSessions = <T extends Role>(
  userRole: T,
  groups: SessionGroups,
) => {
  // Get unique statuses across all groups
  const allStatuses = useMemo(
    () => [...new Set(Object.values(groups).flat())],
    [groups],
  );

  const { sessions, isLoading } = useSessionsByStatus(userRole, allStatuses);

  const sessionsByGroup = useMemo(() => {
    if (!sessions || !sessions.sessions) {
      return {};
    }

    const result: Record<string, SessionType<T>[]> = {};

    // Initialize empty arrays for each group
    Object.keys(groups).forEach((groupKey) => {
      result[groupKey] = [];
    });

    // Group sessions according to the input groups
    sessions.sessions.forEach((session) => {
      Object.entries(groups).forEach(([groupKey, statuses]) => {
        if (statuses.includes(session.status)) {
          result[groupKey]?.push(session as SessionType<T>);
        }
      });
    });

    // Sort sessions in each group by scheduledAt
    Object.values(result).forEach((groupSessions) => {
      groupSessions.sort((a, b) =>
        df.compareAsc(a.sortingTimestamp, b.sortingTimestamp),
      );
    });

    return result;
  }, [groups, sessions]);

  return {
    sessions,
    sessionsByGroup,
    isLoading,
  };
};
